import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  User, BookOpen, Clock, Activity, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Calendar, Award, Target, Brain,
  GraduationCap, BarChart3, FileText, Mail, Phone, MapPin,
  X, ChevronRight, Sparkles, AlertCircle, Lightbulb, ClipboardList
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { BoletimInteligente, SugestaoAtividades, PlanoAcao } from './ComponentesIA';

// Cores
const COLORS = {
  purple: '#8E52AB',
  green: '#10B981',
  blue: '#3B82F6',
  orange: '#F59E0B',
  red: '#EF4444',
  pink: '#EC4899'
};

const CLASSIFICATION_STYLES = {
  excelente: { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30', badge: 'bg-green-500', label: 'Excelente' },
  estavel: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', badge: 'bg-blue-500', label: 'Estável' },
  atencao: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500/30', badge: 'bg-orange-500', label: 'Atenção' },
  critico: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', badge: 'bg-red-500', label: 'Crítico' }
};

// Score Circle Component
function ScoreCircle({ score, size = 'md', label, showTrend, trend }) {
  const sizeMap = {
    sm: { container: 'w-14 h-14', text: 'text-lg', ring: 3 },
    md: { container: 'w-20 h-20', text: 'text-2xl', ring: 4 },
    lg: { container: 'w-28 h-28', text: 'text-3xl', ring: 5 }
  };
  
  const getColor = (s) => {
    if (s >= 80) return COLORS.green;
    if (s >= 60) return COLORS.blue;
    if (s >= 40) return COLORS.orange;
    return COLORS.red;
  };
  
  const percentage = Math.min(Math.max(score, 0), 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  return (
    <div className="flex flex-col items-center gap-1">
      <div className={`${sizeMap[size].container} relative`}>
        <svg className="w-full h-full transform -rotate-90">
          <circle cx="50%" cy="50%" r="45%" fill="none" stroke="rgba(139, 92, 246, 0.2)" strokeWidth={sizeMap[size].ring} />
          <circle
            cx="50%" cy="50%" r="45%"
            fill="none"
            stroke={getColor(score)}
            strokeWidth={sizeMap[size].ring}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${sizeMap[size].text} font-bold text-white`}>
            {Math.round(score)}
          </span>
        </div>
      </div>
      {label && <span className="text-xs text-purple-300/70 font-medium">{label}</span>}
      {showTrend && trend && (
        <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-green-400' : trend < 0 ? 'text-red-400' : 'text-purple-300/50'}`}>
          {trend > 0 ? <TrendingUp className="w-3 h-3" /> : trend < 0 ? <TrendingDown className="w-3 h-3" /> : null}
          <span>{trend > 0 ? '+' : ''}{trend}%</span>
        </div>
      )}
    </div>
  );
}

// Tab de Visão Geral
function TabVisaoGeral({ aluno, scores }) {
  const classStyle = CLASSIFICATION_STYLES[scores?.classificacao] || CLASSIFICATION_STYLES.estavel;
  
  return (
    <div className="space-y-6">
      {/* Scores por Dimensão */}
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Brain className="w-4 h-4 text-purple-400" />
            Scores por Dimensão
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4">
            <ScoreCircle score={scores?.score_academico || 50} size="sm" label="Acadêmico" />
            <ScoreCircle score={scores?.score_frequencia || 85} size="sm" label="Frequência" />
            <ScoreCircle score={scores?.score_comportamental || 95} size="sm" label="Comportamental" />
            <ScoreCircle score={scores?.score_total || 60} size="sm" label="Geral" />
          </div>
        </CardContent>
      </Card>
      
      {/* Indicadores de Risco */}
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <AlertTriangle className="w-4 h-4 text-orange-400" />
            Indicadores de Risco
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <RiskIndicator label="Risco de Reprovação" level={scores?.risco_reprovacao || 'baixo'} />
          <RiskIndicator label="Risco de Queda" level={scores?.risco_queda || 'baixo'} />
          <RiskIndicator label="Risco de Desengajamento" level={scores?.risco_desengajamento || 'baixo'} />
        </CardContent>
      </Card>
      
      {/* Alertas Ativos */}
      {scores?.alertas && scores.alertas.length > 0 && (
        <Card className="bg-slate-800/50 border-red-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <AlertCircle className="w-4 h-4 text-red-400" />
              Alertas Ativos
              <Badge className="bg-red-500 text-white text-xs">{scores.alertas.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {scores.alertas.map((alerta, idx) => (
              <div key={idx} className="flex items-start gap-2 p-2 rounded bg-red-500/10 border border-red-500/20">
                <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white text-sm font-medium">{alerta.tipo}</p>
                  <p className="text-purple-300/70 text-xs">{alerta.mensagem}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Indicador de Risco
function RiskIndicator({ label, level }) {
  const levels = {
    baixo: { width: 25, color: 'bg-green-500', text: 'Baixo' },
    medio: { width: 50, color: 'bg-yellow-500', text: 'Médio' },
    alto: { width: 75, color: 'bg-orange-500', text: 'Alto' },
    critico: { width: 100, color: 'bg-red-500', text: 'Crítico' }
  };
  
  const config = levels[level] || levels.baixo;
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-purple-200 text-sm">{label}</span>
        <Badge className={`${config.color} text-white text-xs`}>{config.text}</Badge>
      </div>
      <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden">
        <div 
          className={`h-full ${config.color} transition-all duration-500`}
          style={{ width: `${config.width}%` }}
        />
      </div>
    </div>
  );
}

// Tab de Notas
function TabNotas({ alunoId, notas, loading }) {
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <Skeleton key={i} className="h-24 w-full bg-slate-700" />
        ))}
      </div>
    );
  }
  
  if (!notas || notas.length === 0) {
    return (
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardContent className="py-8 text-center">
          <BookOpen className="w-12 h-12 text-purple-300/30 mx-auto mb-3" />
          <p className="text-purple-300/70">Nenhuma nota registrada</p>
        </CardContent>
      </Card>
    );
  }
  
  // Agrupar notas por disciplina
  const notasPorDisciplina = notas.reduce((acc, nota) => {
    const disciplina = nota.subject_name || nota.subject_id || 'Sem disciplina';
    if (!acc[disciplina]) {
      acc[disciplina] = [];
    }
    acc[disciplina].push(nota);
    return acc;
  }, {});
  
  return (
    <div className="space-y-4">
      {Object.entries(notasPorDisciplina).map(([disciplina, notasDisciplina]) => {
        const media = notasDisciplina.reduce((acc, n) => acc + (n.grade_value || 0), 0) / notasDisciplina.length;
        
        return (
          <Card key={disciplina} className="bg-slate-800/50 border-purple-500/20">
            <CardContent className="py-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-purple-400" />
                  <span className="text-white font-medium">{disciplina}</span>
                </div>
                <div className={`px-3 py-1 rounded-lg text-white font-bold ${
                  media >= 7 ? 'bg-green-500' : media >= 5 ? 'bg-yellow-500' : 'bg-red-500'
                }`}>
                  {media.toFixed(1)}
                </div>
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                {notasDisciplina.map((nota, idx) => (
                  <div key={idx} className="bg-slate-700/50 rounded p-2 text-center">
                    <p className="text-purple-300/50 text-xs">{nota.trimester || nota.evaluation_type || `Nota ${idx + 1}`}</p>
                    <p className={`text-lg font-bold ${
                      nota.grade_value >= 7 ? 'text-green-400' : 
                      nota.grade_value >= 5 ? 'text-yellow-400' : 'text-red-400'
                    }`}>
                      {nota.grade_value?.toFixed(1) || '-'}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// Tab de Frequência
function TabFrequencia({ alunoId, frequencia, loading }) {
  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full bg-slate-700" />
        <Skeleton className="h-24 w-full bg-slate-700" />
      </div>
    );
  }
  
  const { total_aulas = 0, presencas = 0, faltas = 0, justificadas = 0, taxa = 0 } = frequencia || {};
  
  return (
    <div className="space-y-4">
      {/* Resumo */}
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-purple-400" />
              <span className="text-white font-medium">Taxa de Frequência</span>
            </div>
            <div className={`text-3xl font-bold ${
              taxa >= 75 ? 'text-green-400' : taxa >= 60 ? 'text-yellow-400' : 'text-red-400'
            }`}>
              {taxa.toFixed(1)}%
            </div>
          </div>
          
          <Progress value={taxa} className="h-3 bg-slate-700" />
          
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{total_aulas}</p>
              <p className="text-purple-300/50 text-xs">Total Aulas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">{presencas}</p>
              <p className="text-purple-300/50 text-xs">Presenças</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-400">{faltas}</p>
              <p className="text-purple-300/50 text-xs">Faltas</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-400">{justificadas}</p>
              <p className="text-purple-300/50 text-xs">Justificadas</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Alerta se frequência baixa */}
      {taxa < 75 && (
        <Card className="bg-red-500/10 border-red-500/30">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Frequência Abaixo do Mínimo</p>
                <p className="text-purple-300/70 text-sm mt-1">
                  A frequência mínima exigida é de 75%. Este aluno precisa de acompanhamento para evitar reprovação por falta.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Tab de Evolução
function TabEvolucao({ alunoId, evolucao, loading }) {
  if (loading) {
    return <Skeleton className="h-64 w-full bg-slate-700" />;
  }
  
  if (!evolucao || evolucao.length === 0) {
    return (
      <Card className="bg-slate-800/50 border-purple-500/20">
        <CardContent className="py-8 text-center">
          <TrendingUp className="w-12 h-12 text-purple-300/30 mx-auto mb-3" />
          <p className="text-purple-300/70">Sem dados de evolução disponíveis</p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="bg-slate-800/50 border-purple-500/20">
      <CardHeader className="pb-2">
        <CardTitle className="text-white flex items-center gap-2 text-base">
          <TrendingUp className="w-4 h-4 text-purple-400" />
          Evolução do Score ao Longo do Tempo
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={evolucao} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.purple} stopOpacity={0.4}/>
                  <stop offset="95%" stopColor={COLORS.purple} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(139, 92, 246, 0.1)" />
              <XAxis 
                dataKey="periodo" 
                stroke="rgba(139, 92, 246, 0.5)"
                tick={{ fill: 'rgba(139, 92, 246, 0.7)', fontSize: 12 }}
              />
              <YAxis 
                stroke="rgba(139, 92, 246, 0.5)"
                tick={{ fill: 'rgba(139, 92, 246, 0.7)', fontSize: 12 }}
                domain={[0, 100]}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1E293B', 
                  border: '1px solid rgba(139, 92, 246, 0.3)',
                  borderRadius: '8px',
                  color: '#fff'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="score" 
                stroke={COLORS.purple} 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorScore)" 
                name="Score"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

// Componente Principal do Modal
export default function ModalAluno({ open, onClose, aluno }) {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('visao-geral');
  const [detalhes, setDetalhes] = useState(null);
  const [notas, setNotas] = useState([]);
  const [frequencia, setFrequencia] = useState(null);
  const [evolucao, setEvolucao] = useState([]);
  
  useEffect(() => {
    if (open && aluno?.id) {
      loadDetalhes();
    }
  }, [open, aluno?.id]);
  
  const loadDetalhes = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/dnacore/v2/aluno/${aluno.id}/detalhes`);
      setDetalhes(res.data);
      setNotas(res.data.notas || []);
      setFrequencia(res.data.frequencia || {});
      setEvolucao(res.data.evolucao || []);
    } catch (error) {
      console.error('Erro ao carregar detalhes:', error);
      // Usar dados básicos do aluno se endpoint não existir
      setDetalhes({
        aluno: aluno,
        scores: {
          score_total: aluno.score_total,
          score_academico: aluno.score_academico,
          score_frequencia: aluno.score_frequencia,
          score_comportamental: aluno.score_comportamental,
          classificacao: aluno.classificacao,
          risco_reprovacao: aluno.risco_reprovacao,
          risco_queda: aluno.risco_queda
        }
      });
    } finally {
      setLoading(false);
    }
  };
  
  if (!aluno) return null;
  
  const scores = detalhes?.scores || aluno;
  const classStyle = CLASSIFICATION_STYLES[scores?.classificacao] || CLASSIFICATION_STYLES.estavel;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900/50 to-slate-900 border-purple-500/30 p-0" data-testid="modal-aluno">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900/90 backdrop-blur-xl border-b border-purple-500/20 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              {/* Avatar */}
              <div className="relative">
                {aluno.foto ? (
                  <img 
                    src={aluno.foto} 
                    alt={aluno.nome}
                    className="w-20 h-20 rounded-full object-cover border-4 border-purple-500/30"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-purple-400 flex items-center justify-center text-white text-3xl font-bold border-4 border-purple-500/30">
                    {aluno.nome?.charAt(0) || '?'}
                  </div>
                )}
                <div className={`absolute -bottom-1 -right-1 w-7 h-7 rounded-full ${classStyle.badge} border-4 border-slate-900 flex items-center justify-center`}>
                  {scores?.classificacao === 'excelente' && <CheckCircle className="w-4 h-4 text-white" />}
                  {scores?.classificacao === 'critico' && <AlertTriangle className="w-4 h-4 text-white" />}
                  {scores?.classificacao === 'atencao' && <AlertCircle className="w-4 h-4 text-white" />}
                </div>
              </div>
              
              {/* Info */}
              <div>
                <h2 className="text-2xl font-bold text-white">{aluno.nome}</h2>
                <p className="text-purple-300/70">{aluno.turma}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge className={`${classStyle.bg} ${classStyle.text} border ${classStyle.border}`}>
                    {classStyle.label}
                  </Badge>
                  <Badge className="bg-slate-700 text-purple-300">
                    Mat: {aluno.matricula}
                  </Badge>
                </div>
              </div>
            </div>
            
            {/* Score Principal */}
            <div className="text-center">
              <ScoreCircle score={scores?.score_total || 50} size="lg" />
              <p className="text-purple-300/70 text-sm mt-1">Score DNACore</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full bg-slate-800/50 border border-purple-500/20 p-1 rounded-xl mb-6 flex-wrap h-auto gap-1">
              <TabsTrigger 
                value="visao-geral"
                className="flex-1 min-w-[90px] data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm"
              >
                <Target className="w-4 h-4 mr-1 sm:mr-2" />
                Visão
              </TabsTrigger>
              <TabsTrigger 
                value="notas"
                className="flex-1 min-w-[70px] data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm"
              >
                <BookOpen className="w-4 h-4 mr-1 sm:mr-2" />
                Notas
              </TabsTrigger>
              <TabsTrigger 
                value="frequencia"
                className="flex-1 min-w-[70px] data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm"
              >
                <Clock className="w-4 h-4 mr-1 sm:mr-2" />
                Freq.
              </TabsTrigger>
              <TabsTrigger 
                value="evolucao"
                className="flex-1 min-w-[70px] data-[state=active]:bg-purple-600 data-[state=active]:text-white rounded-lg text-xs sm:text-sm"
              >
                <TrendingUp className="w-4 h-4 mr-1 sm:mr-2" />
                Evol.
              </TabsTrigger>
              <TabsTrigger 
                value="boletim"
                className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-500 data-[state=active]:text-white rounded-lg text-xs sm:text-sm"
              >
                <Brain className="w-4 h-4 mr-1 sm:mr-2" />
                IA
              </TabsTrigger>
              <TabsTrigger 
                value="atividades"
                className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-green-600 data-[state=active]:to-emerald-500 data-[state=active]:text-white rounded-lg text-xs sm:text-sm"
              >
                <Lightbulb className="w-4 h-4 mr-1 sm:mr-2" />
                Ativ.
              </TabsTrigger>
              <TabsTrigger 
                value="plano"
                className="flex-1 min-w-[80px] data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-600 data-[state=active]:to-red-500 data-[state=active]:text-white rounded-lg text-xs sm:text-sm"
              >
                <ClipboardList className="w-4 h-4 mr-1 sm:mr-2" />
                Plano
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="visao-geral">
              <TabVisaoGeral aluno={aluno} scores={scores} />
            </TabsContent>
            
            <TabsContent value="notas">
              <TabNotas alunoId={aluno.id} notas={notas} loading={loading} />
            </TabsContent>
            
            <TabsContent value="frequencia">
              <TabFrequencia alunoId={aluno.id} frequencia={frequencia} loading={loading} />
            </TabsContent>
            
            <TabsContent value="evolucao">
              <TabEvolucao alunoId={aluno.id} evolucao={evolucao} loading={loading} />
            </TabsContent>
            
            <TabsContent value="boletim">
              <BoletimInteligente studentId={aluno.id} studentName={aluno.nome} />
            </TabsContent>
            
            <TabsContent value="atividades">
              <SugestaoAtividades studentId={aluno.id} studentName={aluno.nome} />
            </TabsContent>
            
            <TabsContent value="plano">
              <PlanoAcao studentId={aluno.id} studentName={aluno.nome} />
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
